import unescapedText from 'I18n/utils/unescapedText';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { useDebounceValue } from 'marketplace-ui-common/hooks/useDebounceValue';
import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { UsageScreenProvider, useTrackMarketplaceEvent } from 'marketplace-ui-tracking';
import { getMarketplaceInteractionEvent } from 'marketplace-ui-tracking/events/marketplace';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AUTOCOMPLETE_QUERY as PUBLIC_AUTOCOMPLETE_QUERY } from '../../../__generated__/chirp/com/hubspot/query/understanding/mkpl/autocomp/rpc/MarketplaceAutocompletePublicRpc.dfc.new';
import { AUTOCOMPLETE_QUERY } from '../../../__generated__/chirp/com/hubspot/query/understanding/mkpl/autocomp/rpc/MarketplaceAutocompleteRpc.dfc.new';
import { Subscreens } from '../../constants/usage';
import { usePersonalizationQuery } from '../../hooks/usePersonalizationQuery';
import { useTrackAutocompleteSearchResultEvent } from '../../hooks/useTrackAutocompleteSearchResultEvent';
import { LISTBOX_ID } from '../constants';
import { buildSearchUrl, navigateToSearchResults } from '../utils/navigation';
import { SearchDropdown } from './SearchDropdown';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const getOptionId = idx => `search-bar-dropdown-option-${idx}`;
export function SearchBar() {
  return /*#__PURE__*/_jsx(UsageScreenProvider, {
    subscreen: Subscreens.SEARCH_BAR,
    children: /*#__PURE__*/_jsx(SearchBarContent, {})
  });
}
function SearchBarContent() {
  var _searchParams$get;
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const noResultsTrackedRef = useRef(null);
  const searchParams = useSearchParams();
  const [searchTerm, setSearchTerm] = useState((_searchParams$get = searchParams.get('search')) !== null && _searchParams$get !== void 0 ? _searchParams$get : '');
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const debouncedSearchTerm = useDebounceValue(searchTerm, 300);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [previousResults, setPreviousResults] = useState([]);
  const debouncing = searchTerm !== debouncedSearchTerm;
  const {
    loading,
    data,
    error
  } = usePersonalizationQuery({
    app: AUTOCOMPLETE_QUERY,
    public: PUBLIC_AUTOCOMPLETE_QUERY,
    options: {
      skip: !debouncedSearchTerm,
      variables: {
        queryTerm: debouncedSearchTerm,
        fallbackLanguage: 'en',
        useSpellingCorrections: true,
        limit: 10
      }
    }
  });
  useEffect(() => {
    var _data$result$autocomp, _data$result;
    const newResults = (_data$result$autocomp = data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 ? void 0 : _data$result.autocompleteResultItems) !== null && _data$result$autocomp !== void 0 ? _data$result$autocomp : [];
    if (newResults.length > 0 && !loading) {
      setPreviousResults(newResults);
    }
  }, [data, loading]);
  const options = useMemo(() => {
    var _data$result$autocomp2, _data$result2;
    const currentResults = (_data$result$autocomp2 = data === null || data === void 0 || (_data$result2 = data.result) === null || _data$result2 === void 0 ? void 0 : _data$result2.autocompleteResultItems) !== null && _data$result$autocomp2 !== void 0 ? _data$result$autocomp2 : [];
    if (currentResults.length > 0 && !loading) {
      return currentResults;
    }
    if ((loading || debouncing) && previousResults.length > 0) {
      return previousResults;
    }
    if (searchTerm && !debouncing && !loading) {
      return [{
        resultType: 'QUERY',
        term: searchTerm,
        language: 'en',
        sourceTerm: searchTerm,
        category: [],
        productTypes: [],
        sourceIds: []
      }];
    }
    return [];
  }, [data, searchTerm, loading, debouncing, previousResults]);
  const closeDropdown = () => {
    setOpen(false);
    setActiveIndex(-1);
  };
  const {
    trackAutocompleteSearchResultClickedEvent
  } = useTrackAutocompleteSearchResultEvent({
    resultCount: options.length,
    searchTerm
  });
  const handleResultSelect = (item, resultIndex) => {
    var _inputRef$current;
    switch (item.resultType) {
      case 'QUERY':
        setSearchTerm(item.term || '');
        break;
      case 'LISTING':
      case 'CATEGORY':
      case 'COLLECTION':
      default:
        setSearchTerm('');
        break;
    }
    trackAutocompleteSearchResultClickedEvent(item, resultIndex !== null && resultIndex !== void 0 ? resultIndex : activeIndex);
    navigateToSearchResults(item, history);
    closeDropdown();
    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.blur();
  };
  const scrollIntoView = index => {
    setTimeout(() => {
      const activeElement = document.getElementById(getOptionId(index));
      if (activeElement) {
        activeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }, 0);
  };
  const moveDown = () => {
    setActiveIndex(prev => {
      const newIndex = (prev + 1) % options.length;
      scrollIntoView(newIndex);
      return newIndex;
    });
  };
  const moveUp = () => {
    setActiveIndex(prev => {
      const newIndex = (prev - 1 + options.length) % options.length;
      scrollIntoView(newIndex);
      return newIndex;
    });
  };
  const handleKeyDown = e => {
    const handledKeys = new Set(['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab']);
    if (handledKeys.has(e.key)) {
      e.preventDefault();
    }
    switch (e.key) {
      case 'ArrowDown':
        if (open && options.length > 0) moveDown();
        break;
      case 'Tab':
        if (open && options.length > 0) {
          if (e.shiftKey) {
            moveUp();
          } else {
            moveDown();
          }
        }
        break;
      case 'ArrowUp':
        if (open && options.length > 0) moveUp();
        break;
      case 'Enter':
        if (open && activeIndex >= 0 && activeIndex < options.length) {
          handleResultSelect(options[activeIndex]);
        } else if (searchTerm.trim()) {
          var _inputRef$current2;
          trackMarketplaceEvent(getMarketplaceInteractionEvent({
            action: 'search submitted',
            search: searchTerm
          }));
          history.push(buildSearchUrl(searchTerm));
          closeDropdown();
          (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.blur();
        }
        break;
      case 'Escape':
        closeDropdown();
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    if (options.length === 1 && debouncedSearchTerm && noResultsTrackedRef.current !== debouncedSearchTerm && !loading) {
      trackMarketplaceEvent(getMarketplaceInteractionEvent({
        action: 'search dead end reached',
        search: debouncedSearchTerm
      }));
      noResultsTrackedRef.current = debouncedSearchTerm;
    }
  }, [debouncedSearchTerm, options.length, trackMarketplaceEvent, loading]);
  useEffect(() => {
    if (debouncedSearchTerm && !loading) {
      trackMarketplaceEvent(getMarketplaceInteractionEvent({
        action: 'search bar searched',
        search: searchTerm
      }));
    }
  }, [loading, trackMarketplaceEvent, searchTerm, debouncedSearchTerm]);
  const handleChange = e => {
    setSearchTerm(e.target.value);
    setOpen(true);
  };
  useEffect(() => {
    const handleOutsideClick = e => {
      var _containerRef$current;
      if ((_containerRef$current = containerRef.current) !== null && _containerRef$current !== void 0 && _containerRef$current.contains(e.target)) return;
      closeDropdown();
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);
  useEffect(() => {
    if (!open || (options === null || options === void 0 ? void 0 : options.length) === 0) {
      setActiveIndex(-1);
    }
  }, [options === null || options === void 0 ? void 0 : options.length, open]);
  return /*#__PURE__*/_jsxs("div", {
    ref: containerRef,
    style: {
      width: '100%',
      position: 'relative'
    },
    onKeyDown: handleKeyDown,
    children: [/*#__PURE__*/_jsx(SearchInput, {
      "data-test-id": "storefront-search-input",
      inputRef: inputRef,
      placeholder: unescapedText('marketplaceStorefront.searchBar.placeholder'),
      "aria-label": unescapedText('marketplaceStorefront.searchBar.placeholder'),
      "aria-controls": searchTerm && open ? LISTBOX_ID : undefined,
      "aria-expanded": !!(searchTerm && open),
      "aria-activedescendant": searchTerm && open && activeIndex >= 0 ? getOptionId(activeIndex) : undefined,
      value: searchTerm,
      onChange: handleChange
    }), searchTerm && open && options.length > 0 && /*#__PURE__*/_jsx(SearchDropdown, {
      activeIndex: activeIndex,
      error: !!error,
      onResultSelect: handleResultSelect,
      options: options,
      searchTerm: searchTerm,
      setActiveIndex: setActiveIndex
    })]
  });
}